/*
 * Copyright (c) 2026. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import { fromGender } from "@/src/services/formServiceFunctions";

import React from "react";
import Grid from "@mui/material/Grid";
import summaryStyle from "@/src/components/Form/Common/Style/summaryStyle";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { Box } from "@mui/material";
import { eur } from "@/src/services/formatService";
import { Item } from "@/src/components/Form/FamilyLaw/Alimony/AlimonyExpenses/AlimonyExpenses";
import { fromFloat } from "@/src/services/formServiceFunctions";

type PersonDataProps = {
  formData: AbstractCase | Record<string, any>;
  personType: string;
  title: string;
  translate: Function;
};

const PersonData = ({ formData, personType, title, translate }: PersonDataProps) => {
  const personData = formData[personType];

  return (
    <>
      <Grid sx={summaryStyle.subHeadline} size={12}>
        <Typography variant={"h6"}>{title}</Typography>
      </Grid>
      {personType === "alimonyPartner" && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".person.gender"} />
            Anrede
          </Grid>
          <Grid size={12}>
            {fromGender(personData.person.gender) || "Keine Anrede"}
          </Grid>

          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".person.givenName"} />
            Vorname
          </Grid>
          <Grid size={12}>
            {personData.person.givenName}
          </Grid>

          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".person.familyName"} />
            Nachname
          </Grid>
          <Grid size={12}>
            {personData.person.familyName}
          </Grid>
        </>
      )}
      <Grid sx={summaryStyle.question} size={12}>
        <Box id={personType + ".employmentStatus"} />
        Beschäftigungsstatus
      </Grid>
      <Grid size={12}>
        {translate(`${personType}.employmentStatus.${personData.employmentStatus}`, formData)}
      </Grid>
      {personData.employmentStatus !== "unemployed" && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".netIncome"} />
            Nettoeinkommen
          </Grid>
          <Grid size={12}>
            {eur(personData.netIncome)}
          </Grid>
        </>
      )}
      {personData.otherIncome > 0 && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".otherIncome"} />
            Sonstiges Nettoeinnahmen
          </Grid>
          <Grid size={12}>
            {eur(personData.otherIncome)}
          </Grid>
        </>
      )}
      {formData.basicChoice === "complete" && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".hasMarriageRelatedDisadvantages"} />
            Liegen ehebedingte Nachteile vor?
          </Grid>
          <Grid size={12}>
            {personData.hasMarriageRelatedDisadvantages === "yes" ? "Ja" : "Nein"}
          </Grid>
        </>
      )}
      <Grid sx={summaryStyle.question} size={12}>
        <Box id={personType + ".livingSituation"} />
        Wohnsituation
      </Grid>
      <Grid size={12}>
        {translate(`${personType}.livingSituation.${personData.livingSituation}`, formData)}
      </Grid>
      {personData.livingExpenses !== null && personData.livingSituation === "tenant" && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".livingExpenses"} />
            Monatliche Warmmiete
          </Grid>
          <Grid size={12}>
            {eur(personData.livingExpenses)}
          </Grid>
        </>
      )}
      {personData.monthlyMortgagePayment !== null && personData.livingSituation === "ownerWithMortgage" && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".monthlyImputedRent"} />
            Monatliche Kreditrate
          </Grid>
          <Grid size={12}>
            {eur(personData.monthlyMortgagePayment)}
          </Grid>
        </>
      )}
      {personData.monthlyImputedRent !== null && ["ownerWithMortgage", "ownerOutright", "other"].includes(personData.livingSituation) && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".monthlyImputedRent"} />
            Wohnwert
          </Grid>
          <Grid size={12}>
            {eur(personData.monthlyImputedRent)}
          </Grid>
        </>
      )}
      {personData.otherExpenses !== null && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".otherExpenses"} />
            Sonstige Ausgaben
          </Grid>
          <Grid size={12}>
            {eur(personData.otherExpenses)}
          </Grid>
        </>
      )}
      {personData.expensesList.length > 0 && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".expensesList"} />
            Übersicht der Ausgaben
          </Grid>
          {personData.expensesList.map((item: Item) => (
            <Grid size={12}>
              {translate(`expensesCategory.${item.category}`, formData)} - {item.name}: {eur(fromFloat(item.monthlyAmount))}
            </Grid>
          ))}
        </>
      )}
    </>
  );
};

export default PersonData;
